import api from './api';

/**
 * Auth Service
 * Handles all authentication-related API calls
 */
const authService = {
  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @returns {Promise<{user: Object, token: string}>}
   */
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data.data; // { user, token }
  },
  
  /**
   * Login user
   * @param {Object} credentials - {email, password}
   * @returns {Promise<{user: Object, token: string}>}
   */
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data.data; // { user, token }
  },
  
  /**
   * Get current user (requires authentication)
   * Phase 5: M5.T5.1 - Now returns user + permissions + menu
   * @returns {Promise<{user: Object, menu: Array}>}
   */
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data.data; // { user: {..., permissions: []}, menu: [] }
  },
  
  /**
   * Logout (client-side only, token is stateless)
   * @returns {Promise<void>}
   */
  logout: () => {
    // Token removal handled by Redux action
    // No backend call needed
    return Promise.resolve();
  },
  
  /**
   * Forgot password - Request reset email
   * @param {string} email - User email
   * @returns {Promise<{message: string}>}
   */
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data; // { success, message }
  },
  
  /**
   * Reset password with token
   * @param {string} token - Reset token from URL
   * @param {string} newPassword - New password
   * @returns {Promise<{message: string}>}
   */
  resetPassword: async (token, newPassword) => {
    const response = await api.post(`/auth/reset-password/${token}`, { newPassword });
    return response.data; // { success, message }
  },
  
  /**
   * Send verification email (Phase 3)
   * @returns {Promise<{success: boolean, message: string}>}
   */
  sendVerificationEmail: async () => {
    const response = await api.post('/auth/send-verification-email');
    return response.data;
  },
  
  /**
   * Verify email with token (Phase 3)
   * @param {string} token - Verification token from URL
   * @returns {Promise<{success: boolean, message: string, user: Object}>}
   */
  verifyEmail: async (token) => {
    const response = await api.get(`/auth/verify-email/${token}`);
    return response.data;
  },
  
  /**
   * Resend verification email (Phase 3)
   * @returns {Promise<{success: boolean, message: string}>}
   */
  resendVerificationEmail: async () => {
    const response = await api.post('/auth/resend-verification-email');
    return response.data;
  },
  
  /**
   * Check verification status (Phase 3)
   * @returns {Promise<{success: boolean, data: Object}>}
   */
  checkVerificationStatus: async () => {
    const response = await api.get('/auth/verification-status');
    return response.data;
  }
};

export default authService;

